import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Table from "react-bootstrap/Table";
import Tabs from "react-bootstrap/Tabs";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import "./classroommanagement.css";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "100px 30px" };
const DASHBOARD_ROW_STYLE = { paddingTop: "20px", paddingBottom: "20px" };
const DASHBOARD_COL_STYLE = { backgroundColor: "#A7D588" };
const TOTAL_CARD_STYLE = { backgroundColor: "#24A2B3" };
const PREVIOUS_CARD_STYLE = { backgroundColor: "#E96CB4" };
const PRESENT_CARD_STYLE = { backgroundColor: "#7F2DA2" };
const UPCOMING_CARD_STYLE = { backgroundColor: "#C78B62" };
const PARTICIPANTS_CARD_STYLE = { backgroundColor: "#4C87B8" };
const TABLE_CONTAINER_STYLE = { paddingTop: "50px" };
const TABLE_STYLE = { textAlign: "center" };
const ACTION_CELL_STYLE = {
	fontSize: "20px",
	textAlign: "center",
	fontFamily: "Roboto, sans-serif",
};
const VIEW_BUTTON_STYLE = {
	backgroundColor: "#00ccff",
	border: "none",
	borderRadius: "4px",
	margin: "5px",
	color: "#fff",
};
const DELETE_BUTTON_STYLE = {
	background: "#E23F1C",
	fontSize: "13px",
	borderRadius: "4px",
	border: "none",
};

const TAB_TITLES = {
	previous: "<< Previous Classes",
	present: "Present Classes",
	upcoming: "Upcoming Classes >>",
};

const NO_DATA_MESSAGES = {
	previous: "No previous classes available at the moment.",
	present: "No present classes available at the moment.",
	upcoming: "No upcoming classes available at the moment.",
};

const Classroommanagement = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();

	// State
	const [allClasses, setAllClasses] = useState([]);
	const [totalParticipants, setTotalParticipants] = useState(0);

	// Date calculations
	const todayDate = moment().startOf("day");
	const previousDate = moment().subtract(1, "days").startOf("day");
	const upcomingDate = moment().add(1, "days").startOf("day");

	// Helper function to categorize classes
	const getClassCategory = useCallback(
		(classDate) => {
			const classMoment = moment(classDate, "DD/MM/YYYY h:mm A").startOf("day");

			if (classMoment.isSame(todayDate, "day")) {
				return "present";
			} else if (classMoment.isBefore(todayDate, "day")) {
				return "previous";
			} else if (classMoment.isAfter(todayDate, "day")) {
				return "upcoming";
			}
			return "";
		},
		[todayDate]
	);

	// Fetch data
	useEffect(() => {
		const fetchAllClasses = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

				const res = await getDataAPI("getAllClasses", auth.token);
				setAllClasses(res.data.scheduleClasses || []);
				setTotalParticipants(res.data.totalparticipants || 0);
			} catch (err) {
				console.error("Error fetching classes:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch classes data" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchAllClasses();
	}, [auth.token, dispatch]);

	// Delete handler
	const handleDelete = useCallback(
		async (classId) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await getDataAPI(
					`inActivescheduleclass/${classId}`,
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error deleting class:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: err.response?.data?.msg || "Error deleting class" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Memoized dashboard cards
	const dashboardCards = useMemo(
		() => [
			{
				key: "total",
				label: "Total Classes",
				style: TOTAL_CARD_STYLE,
				value: allClasses.length,
			},
			{
				key: "previous",
				label: "Previous Classes",
				style: PREVIOUS_CARD_STYLE,
				value: allClasses.filter(
					(cls) => getClassCategory(cls.formattedDateTime) === "previous"
				).length,
			},
			{
				key: "present",
				label: "Present Classes",
				style: PRESENT_CARD_STYLE,
				value: allClasses.filter(
					(cls) => getClassCategory(cls.formattedDateTime) === "present"
				).length,
			},
			{
				key: "upcoming",
				label: "Upcoming Classes",
				style: UPCOMING_CARD_STYLE,
				value: allClasses.filter(
					(cls) => getClassCategory(cls.formattedDateTime) === "upcoming"
				).length,
			},
			{
				key: "participants",
				label: "Total Participants",
				style: PARTICIPANTS_CARD_STYLE,
				value: totalParticipants,
			},
		],
		[allClasses, getClassCategory, totalParticipants]
	);

	// Memoized table rows for each category
	const getTableRows = useCallback(
		(category) => {
			const filteredClasses = allClasses.filter(
				(cls) => getClassCategory(cls.formattedDateTime) === category
			);

			if (filteredClasses.length === 0) {
				return (
					<tr>
						<td colSpan="8">{NO_DATA_MESSAGES[category]}</td>
					</tr>
				);
			}

			return filteredClasses.map((scheduleClasses, index) => (
				<tr key={scheduleClasses.classId || index}>
					<td>{index + 1}</td>
					<td>{scheduleClasses.organisationName}</td>
					<td>{scheduleClasses.formattedDateTime}</td>
					<td>{scheduleClasses.courseName}</td>
					<td>
						{scheduleClasses.instructor &&
							JSON.parse(scheduleClasses.instructor)?.name}
					</td>
					<td>{scheduleClasses.noOfMembers}</td>
					<td>
						<span className="truncate-url" title={scheduleClasses.classURL}>
							<a
								href={scheduleClasses.classURL}
								target="_blank"
								rel="noopener noreferrer">
								{scheduleClasses.classURL}
							</a>
						</span>
					</td>
					<td style={ACTION_CELL_STYLE}>
						<Button
							onClick={() => {
								window.location.href = `/attendancemanagement/${scheduleClasses.classId}`;
							}}
							style={VIEW_BUTTON_STYLE}>
							View
						</Button>
						<Button
							style={DELETE_BUTTON_STYLE}
							className="btn btn-danger mt-2 col-6"
							onClick={() => handleDelete(scheduleClasses.classId)}>
							<i className="fa fa-trash" aria-hidden="true"></i>
						</Button>
					</td>
				</tr>
			));
		},
		[allClasses, getClassCategory, handleDelete]
	);

	// Memoized table rows for each category
	const previousRows = useMemo(() => getTableRows("previous"), [getTableRows]);
	const presentRows = useMemo(() => getTableRows("present"), [getTableRows]);
	const upcomingRows = useMemo(() => getTableRows("upcoming"), [getTableRows]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="button-format">
					<div className="dashboard-text">Super Admin Dashboard</div>
					<a href="/schedule-a-class">
						<button className="addinstructor-btn">+ SCHEDULE A CLASS</button>
					</a>
				</div>
				<div id="">
					<div>
						<div className="row format">
							<Sidenavbar />
							<div
								className="col-8 card-format-analatics"
								style={DASHBOARD_COL_STYLE}>
								<div className="row format" style={DASHBOARD_ROW_STYLE}>
									{dashboardCards.slice(0, 3).map((card) => (
										<div
											key={card.key}
											className="col text-format"
											style={card.style}>
											<div className="text1">{card.value}</div>
											<div className="text1">{card.label}</div>
										</div>
									))}
								</div>
								<div className="row format" style={{ paddingBottom: "20px" }}>
									{dashboardCards.slice(3).map((card) => (
										<div
											key={card.key}
											className="col text-format"
											style={card.style}>
											<div className="text1">{card.value}</div>
											<div className="text1">{card.label}</div>
										</div>
									))}
								</div>
							</div>
						</div>

						<div className="row" style={TABLE_CONTAINER_STYLE}>
							<h4 className="instructorlist-text">List Of Schedule Classes</h4>
							<div id="listclasses" className="col">
								<Tabs defaultActiveKey="second">
									<Tab eventKey="first" title={TAB_TITLES.previous}>
										<div style={{ paddingTop: "50px" }}>
											<div className="table-responsive">
												<Table bordered striped style={TABLE_STYLE}>
													<thead id="pl">
														<tr>
															<th>S.No</th>
															<th>Organisation Name</th>
															<th>Date and Time</th>
															<th>Course Name</th>
															<th>Instructor</th>
															<th>No. Of Numbers</th>
															<th>Class URL</th>
															<th>Action</th>
														</tr>
													</thead>
													<tbody>{previousRows}</tbody>
												</Table>
											</div>
										</div>
									</Tab>

									<Tab eventKey="second" title={TAB_TITLES.present}>
										<div style={{ paddingTop: "50px" }}>
											<div className="table-responsive">
												<Table bordered striped style={TABLE_STYLE}>
													<thead id="pl">
														<tr>
															<th>S.No</th>
															<th>Organisation Name</th>
															<th>Date and Time</th>
															<th>Course Name</th>
															<th>Instructor</th>
															<th>No. Of Numbers</th>
															<th>Class URL</th>
															<th>Action</th>
														</tr>
													</thead>
													<tbody>{presentRows}</tbody>
												</Table>
											</div>
										</div>
									</Tab>

									<Tab eventKey="third" title={TAB_TITLES.upcoming}>
										<div style={{ paddingTop: "50px" }}>
											<div className="table-responsive">
												<Table bordered striped style={TABLE_STYLE}>
													<thead id="pl">
														<tr>
															<th>S.No</th>
															<th>Organisation Name</th>
															<th>Date and Time</th>
															<th>Course Name</th>
															<th>Instructor</th>
															<th>No. Of Numbers</th>
															<th>Class URL</th>
															<th>Action</th>
														</tr>
													</thead>
													<tbody>{upcomingRows}</tbody>
												</Table>
											</div>
										</div>
									</Tab>
								</Tabs>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default Classroommanagement;
